import { useState, useCallback, useContext, createContext } from "react"
import Cookies from "js-cookie"

const API = `${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/agency`

function authHeader() {
    // Auth token lives in the `token` cookie (see billingApi.js), not localStorage.
    const t = Cookies.get("token")
    return t ? { Authorization: `Bearer ${t}` } : {}
}

function workspaceHeader() {
    const wsId = localStorage.getItem("activeWorkspaceId")
    return wsId ? { "X-Workspace-ID": wsId } : {}
}

function headers(json = false) {
    return {
        ...authHeader(),
        ...workspaceHeader(),
        ...(json ? { "Content-Type": "application/json" } : {}),
    }
}

// ── Workspace Context ─────────────────────────────────────────────────────────

export const WorkspaceContext = createContext({
    currentWorkspace: null,
    isAgency: false,
    agencyData: null,
    setWorkspace: () => {},
    clearWorkspace: () => {},
})

export function useWorkspace() {
    return useContext(WorkspaceContext)
}

// ── Main hook ─────────────────────────────────────────────────────────────────

export function useAgency() {
    const [agency, setAgency] = useState(null)
    const [workspaces, setWorkspaces] = useState([])
    const [members, setMembers] = useState([])
    const [insights, setInsights] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Load agency context (agency + workspaces the user can see)
    const fetchAgency = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await fetch(API, { headers: headers() })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || "Failed to load agency")
            setAgency(data.agency)
            setWorkspaces(data.workspaces || [])
            return data
        } catch (e) {
            setError(e.message)
            return null
        } finally {
            setLoading(false)
        }
    }, [])

    // Create the agency (first-time setup)
    const createAgency = useCallback(async ({ name, billingEmail, currency }) => {
        const res = await fetch(API, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify({ name, billingEmail, currency }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to create agency")
        setAgency(data.agency)
        return data
    }, [])

    // Update agency settings
    const updateAgency = useCallback(async (updates) => {
        const res = await fetch(API, {
            method: "PATCH",
            headers: headers(true),
            body: JSON.stringify(updates),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to update agency")
        setAgency(data.agency)
        return data
    }, [])

    // Workspace CRUD
    const createWorkspace = useCallback(async ({ name, clientInfo, branding, billing }) => {
        const res = await fetch(`${API}/client-workspaces`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify({ name, clientInfo, branding, billing }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to create workspace")
        setWorkspaces(prev => [...prev, data.workspace])
        return data.workspace
    }, [])

    const updateWorkspace = useCallback(async (id, updates) => {
        const res = await fetch(`${API}/client-workspaces/${id}`, {
            method: "PATCH",
            headers: headers(true),
            body: JSON.stringify(updates),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to update workspace")
        setWorkspaces(prev => prev.map(ws => ws._id === id ? data.workspace : ws))
        return data.workspace
    }, [])

    const archiveWorkspace = useCallback(async (id, reason) => {
        const res = await fetch(`${API}/client-workspaces/${id}`, {
            method: "DELETE",
            headers: headers(true),
            body: JSON.stringify({ reason }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to archive workspace")
        setWorkspaces(prev => prev.filter(ws => ws._id !== id))
        return data
    }, [])

    const restoreWorkspace = useCallback(async (id) => {
        const res = await fetch(`${API}/client-workspaces/${id}/restore`, {
            method: "POST",
            headers: headers(true),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to restore workspace")
        return data.workspace
    }, [])

    const listArchivedWorkspaces = useCallback(async () => {
        const res = await fetch(`${API}/client-workspaces?status=archived`, { headers: headers() })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to load archived workspaces")
        return data.workspaces || []
    }, [])

    // Switch active workspace — updates localStorage + returns workspace details
    const switchWorkspace = useCallback(async (workspaceId) => {
        const res = await fetch(`${API}/switch`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify({ workspaceId }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to switch workspace")
        localStorage.setItem("activeWorkspaceId", workspaceId)
        return data.workspace
    }, [])

    // Members
    const fetchMembers = useCallback(async () => {
        const res = await fetch(`${API}/members`, { headers: headers() })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to load members")
        setMembers(data.members || [])
        return data.members
    }, [])

    const inviteMember = useCallback(async ({ email, role, accessScope }) => {
        const res = await fetch(`${API}/members/invite`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify({ email, role, accessScope }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to invite member")
        return data
    }, [])

    const updateMember = useCallback(async (memberId, updates) => {
        const res = await fetch(`${API}/members/${memberId}`, {
            method: "PATCH",
            headers: headers(true),
            body: JSON.stringify(updates),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to update member")
        setMembers(prev => prev.map(m => m._id === memberId ? data.member : m))
        return data.member
    }, [])

    // Insights
    const fetchInsights = useCallback(async () => {
        const res = await fetch(`${API}/insights`, { headers: headers() })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to load insights")
        setInsights(data)
        return data
    }, [])

    // Downgrade handler
    const handleDowngrade = useCallback(async (keepWorkspaceId) => {
        const res = await fetch(`${API}/downgrade`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify({ keepWorkspaceId }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || "Failed to process downgrade")
        return data
    }, [])

    return {
        agency, workspaces, members, insights, loading, error,
        fetchAgency, createAgency, updateAgency,
        createWorkspace, updateWorkspace, archiveWorkspace, restoreWorkspace, listArchivedWorkspaces,
        switchWorkspace,
        fetchMembers, inviteMember, updateMember,
        fetchInsights,
        handleDowngrade,
    }
}
